<script setup lang="ts">
import { CheckCheck, Copy, FileText, Pencil } from 'lucide-vue-next'
import { formatMessage } from '~/lib/formatMessage'
import { chatDocumentLink } from '~/utils/chatDocumentLinks'
import type { WebChatMessage } from '~/types/web-chat'

const props = defineProps<{
  msg: WebChatMessage
  /** A shared snapshot (MAR-1417): nothing to edit, so no edit button. */
  readonly?: boolean
}>()

const emit = defineEmits<{
  resend: [messageId: string, content: string]
}>()

const { t } = useI18n()

/**
 * Whether the action buttons are pinned open by a tap.
 *
 * Separate from hover rather than replacing it: hover keeps working with a pointer, and touch —
 * where `group-hover` never fires at all — gets a way in.
 */
const revealed = ref(false)
const editing = ref(false)
const editText = ref('')
const editTextarea = ref<HTMLTextAreaElement | null>(null)
const copied = ref(false)

function startEdit() {
  // The tap that opened the buttons has done its job; leaving it set would pin them open again the
  // moment the edit is cancelled.
  revealed.value = false
  editing.value = true
  editText.value = props.msg.content
  nextTick(() => {
    const el = editTextarea.value
    if (el) {
      el.focus()
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    }
  })
}

function cancelEdit() {
  editing.value = false
  editText.value = ''
}

function submitEdit() {
  const content = editText.value.trim()
  if (!content) return
  editing.value = false
  editText.value = ''
  emit('resend', props.msg.id, content)
}

function onEditKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    submitEdit()
  }
  if (e.key === 'Escape') {
    cancelEdit()
  }
}

function autoResize(e: Event) {
  const el = e.target as HTMLTextAreaElement
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

async function copyMessage() {
  await navigator.clipboard.writeText(props.msg.content)
  copied.value = true
  setTimeout(() => { copied.value = false }, 1500)
}
</script>

<template>
  <!-- No avatar (MAR-1057): the bubble on the right already says who wrote it. -->
  <div class="group/msg flex items-start gap-2 flex-row-reverse">

    <!-- Editing state (wide) -->
    <div v-if="editing" class="w-[90%] min-w-0 flex flex-col gap-1.5">
      <!-- Attached documents -->
      <div
        v-if="msg.attachments?.length"
        data-ph-mask
        class="flex flex-wrap gap-1.5 justify-end"
      >
        <NuxtLink
          v-for="att in msg.attachments"
          :key="att.id"
          :to="chatDocumentLink(att.id)"
          class="doc-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border border-border text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors max-w-[200px]"
        >
          <FileText class="h-3 w-3 shrink-0" />
          <span class="truncate">{{ att.name }}</span>
        </NuxtLink>
      </div>
      <textarea
        :id="'edit-' + msg.id"
        ref="editTextarea"
        v-model="editText"
        class="w-full rounded-md border border-border bg-muted/50 px-3 py-2 text-sm leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-primary"
        rows="1"
        @keydown="onEditKeydown($event)"
        @input="autoResize"
      />
      <div class="flex items-center gap-1.5 justify-end">
        <button
          type="button"
          class="px-2.5 py-1 text-xs rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
          @click="cancelEdit"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="button"
          class="px-2.5 py-1 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          @click="submitEdit"
        >
          {{ t('common.send') }}
        </button>
      </div>
    </div>

    <!-- Display state -->
    <div v-else class="max-w-[85%] min-w-0">
      <!-- Attached documents -->
      <div
        v-if="msg.attachments?.length"
        data-ph-mask
        class="flex flex-wrap gap-1.5 mb-1.5 justify-end"
      >
        <NuxtLink
          v-for="att in msg.attachments"
          :key="att.id"
          :to="chatDocumentLink(att.id)"
          class="doc-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border border-border text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors max-w-[200px]"
        >
          <FileText class="h-3 w-3 shrink-0" />
          <span class="truncate">{{ att.name }}</span>
        </NuxtLink>
      </div>
      <!-- Tapping the bubble reveals the actions, because HOVER DOES NOT EXIST ON TOUCH: on a phone
           `group-hover` never fires, so editing your own message was unreachable there. A tap is
           also a reasonable desktop affordance — it pins the buttons instead of making you keep the
           pointer still — so this is an addition to hover rather than a replacement for it. -->
      <div
        data-user-message
        data-ph-mask
        class="bg-accent-surface rounded-2xl rounded-tr-sm px-3 py-2 cursor-pointer"
        @click="revealed = !revealed"
      >
        <div
          class="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none marcus-prose"
          v-html="formatMessage(msg.content)"
        />
      </div>
      <!-- Action buttons: on hover, or once tapped. -->
      <div
        v-if="msg.content"
        class="transition-opacity flex items-center gap-0.5 mt-0.5 justify-end"
        :class="revealed ? 'opacity-100' : 'opacity-0 group-hover/msg:opacity-100'"
      >
        <button
          v-if="!props.readonly"
          type="button"
          class="p-1 rounded-md hover:bg-accent-lighter text-muted-foreground hover:text-foreground transition-colors"
          :title="t('common.editMessage')"
          @click="startEdit"
        >
          <Pencil class="h-3 w-3" />
        </button>
        <button
          type="button"
          class="p-1 rounded-md hover:bg-accent-lighter text-muted-foreground hover:text-foreground transition-colors"
          :title="t('common.copyToClipboard')"
          @click="copyMessage"
        >
          <CheckCheck v-if="copied" class="h-3 w-3 text-success" />
          <Copy v-else class="h-3 w-3" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "~/assets/css/tailwind.css";
.doc-chip {
  animation: chip-enter 0.2s ease-out;
}

@keyframes chip-enter {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
